// Unified game engine with state management
import { SeededRandom } from './random';
import { 
  PIN_SPARK_MAX_FRAMES,
  MIN_VELOCITY_THRESHOLD, STUCK_VELOCITY_BOOST, CANVAS_WIDTH, 
  SPAWN_OFFSET_FACTOR, SPAWN_HEIGHT_FACTOR, NORMAL_PIN_RESTITUTION, SPECIAL_PIN_EFFECT_CAP,
  SPECIAL_PIN_MIN_SPEED_FACTOR, COLLISION_COOLDOWN_FRAMES,
  SEPARATION_OVERLAP, MAX_HORIZONTAL_SPEED_FACTOR,
  MAX_VERTICAL_SPEED_FACTOR,
  BlinkoMode, BLINKO_LETTERS, BlinkoLetter, LETTER_VERTICAL_OFFSET, LETTER_CAPTURE_RADIUS,
  BlinkoVersion,
  LAUNCHES_PER_GAME
} from './constants';
import { GameEventEmitter, GameEvent, GameEventType } from './events';
import { Ball, Pin } from './config';
import { getBoard } from './board-spec';

export type LetterState = {
  letter: BlinkoLetter;
  binIndex: number;
  x: number;
  y: number;
  active: boolean;
};

export type GameEngineState = {
  balls: Ball[];
  pinFx: Map<number, number>;
  pinSoundCooldown: number;
  mode: BlinkoMode;
  letters: LetterState[];
  ballsLaunched: number;
};

export class BlinkoGameEngine {
  private state: GameEngineState;
  private eventEmitter: GameEventEmitter;
  private pins: Pin[];
  private board: ReturnType<typeof getBoard>;
  private pendingBonus: boolean;
  private version: BlinkoVersion = 'v2';

  constructor() {
    this.eventEmitter = new GameEventEmitter();
    const board = getBoard();
    this.board = board;
    this.pins = board.pins;
    this.state = {
      balls: [],
      pinFx: new Map(),
      pinSoundCooldown: 0,
      mode: 'main',
      letters: [],
      ballsLaunched: 0,
    };
    this.pendingBonus = false;
  }

  private externalLetterControl = false;

  /** v3 owns letter lifetime/expiry AND uses a different lighting rule (bin-landing only),
   *  so it takes over both the state and the activation trigger. */
  public setExternalLetterControl(on: boolean) { this.externalLetterControl = on; }

  public setLettersActive(active: boolean[]) {
    this.state.letters.forEach((l, i) => { l.active = !!active[i]; });
  }

  public setVersion(version: BlinkoVersion) {
    this.version = version;
  }

  /** `explicitBins` lets a caller (v3) pin letters to a chosen layout instead of seeding them. */
  public initializeLetters(seed: number, explicitBins?: number[]) {
    const previousMode = this.state.mode;
    if (previousMode !== 'main') {
      this.state.mode = 'main';
      this.eventEmitter.emit({
        type: 'mode-changed',
        payload: { mode: 'main' },
      });
    }

    const normalizedSeed = seed >>> 0;

    const chosenBins: number[] = [];
    if (explicitBins && explicitBins.length) {
      chosenBins.push(...explicitBins);
    } else {
      const rng = new SeededRandom(normalizedSeed);
      const availableBins = Array.from({ length: this.board.binCount }, (_, index) => index);
      const letterCount = Math.min(BLINKO_LETTERS.length, availableBins.length);
      for (let i = 0; i < letterCount; i++) {
        if (!availableBins.length) break;
        const pick = Math.floor(rng.next() * availableBins.length);
        const [binIndex] = availableBins.splice(pick, 1);
        chosenBins.push(binIndex);
      }
    }
    // ascending so B..S read left-to-right across the board
    chosenBins.sort((a, b) => a - b);

    const baseY = Math.max(
      0,
      this.board.bottomY - LETTER_VERTICAL_OFFSET
    );

    const letters: LetterState[] = [];
    for (let i = 0; i < chosenBins.length; i++) {
      const letter = BLINKO_LETTERS[i];
      if (!letter) continue;
      const binIndex = chosenBins[i];
      letters.push({
        letter,
        binIndex,
        x: this.getBinCenterX(binIndex),
        y: baseY,
        active: false,
      });
    }

    this.state.letters = letters;
    this.state.ballsLaunched = 0;
    this.pendingBonus = false;
  }

  private getBinCenterX(binIndex: number): number {
    return this.board.leftmostBinX + binIndex * this.board.binWidth;
  }

  private transitionToMode(next: BlinkoMode): void {
    if (this.state.mode === next) return;
    this.state.mode = next;
    if (next === 'bonus') {
      this.pendingBonus = false;
      this.state.letters = [];
    }
    this.eventEmitter.emit({
      type: 'mode-changed',
      payload: { mode: next }
    });
  }

  private segmentIntersectsCircle(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    cx: number,
    cy: number,
    radius: number
  ): boolean {
    const dx = x2 - x1;
    const dy = y2 - y1;
    if (dx === 0 && dy === 0) {
      const distSq = (x1 - cx) * (x1 - cx) + (y1 - cy) * (y1 - cy);
      return distSq <= radius * radius;
    }

    const fx = x1 - cx;
    const fy = y1 - cy;
    const a = dx * dx + dy * dy;
    const b = 2 * (fx * dx + fy * dy);
    const c = fx * fx + fy * fy - radius * radius;
    let discriminant = b * b - 4 * a * c;
    if (discriminant < 0) return false;
    discriminant = Math.sqrt(discriminant);
    const t1 = (-b - discriminant) / (2 * a);
    const t2 = (-b + discriminant) / (2 * a);
    return (t1 >= 0 && t1 <= 1) || (t2 >= 0 && t2 <= 1);
  }

  private checkLetterActivation(ball: Ball, prevX: number, prevY: number): void {
    if (this.externalLetterControl) return;
    if (this.state.mode !== 'main' || !this.state.letters.length) return;
    const currentX = ball.x!;
    const currentY = ball.y!;
    const captureRadius = LETTER_CAPTURE_RADIUS + this.board.physics.ballRadius;
    let anyActivated = false;

    for (const letter of this.state.letters) {
      if (letter.active) continue;
      const intersects = this.segmentIntersectsCircle(
        prevX,
        prevY,
        currentX,
        currentY,
        letter.x,
        letter.y,
        captureRadius
      );
      if (!intersects) continue;
      letter.active = true;
      anyActivated = true;
      this.eventEmitter.emit({
        type: 'letter-activated',
        payload: {
          letter: letter.letter,
          binIndex: letter.binIndex,
        },
      });
    }

    if (anyActivated && this.state.letters.every((l) => l.active)) {
      this.pendingBonus = true;
    }
  }

  public getMode(): BlinkoMode {
    return this.state.mode;
  }

  public getLetters(): LetterState[] {
    return this.state.letters.map((letter) => ({ ...letter }));
  }

  // Event system
  on(eventType: GameEventType, callback: (event: GameEvent) => void) {
    this.eventEmitter.on(eventType, callback);
  }

  off(eventType: GameEventType, callback: (event: GameEvent) => void) {
    this.eventEmitter.off(eventType, callback);
  }

  // No scale needed in engine (renderer handles it)

  // Add ball to the game
  addBall(randomSeed: number, spin?: number): void {
    this.state.ballsLaunched += 1;
    this.state.balls.push({
      randomSeed,
      collisionCooldown: 0,
      currentSpecialHits: 0,
      stuckCount: 0,
      spin,
    });
  }

  // Update physics
  update(): void {
    // Update cooldowns
    if (this.state.pinSoundCooldown > 0) this.state.pinSoundCooldown -= 1;

    // Decay pin FX
    if (this.state.pinFx.size) {
      for (const [key, frames] of this.state.pinFx.entries()) {
        const next = frames - 1;
        if (next <= 0) this.state.pinFx.delete(key);
        else this.state.pinFx.set(key, next);
      }
    }

    // Update balls
    const updatedBalls: Ball[] = [];

    this.state.balls.forEach((ball) => {
      const processedBall = this.processBall(ball);
      
      // Check if ball reached bottom (normalized coordinates)
      const bottomY = this.board.bottomY;
      if (processedBall.y! > bottomY) {
        this.handleBallReachedBottom(processedBall);
      } else {
        updatedBalls.push(processedBall);
      }
    });

    this.state.balls = updatedBalls;

    if (
      this.pendingBonus &&
      this.state.mode === 'main' &&
      this.state.balls.length === 0 &&
      this.state.ballsLaunched >= LAUNCHES_PER_GAME &&
      this.state.letters.every((letter) => letter.active)
    ) {
      this.pendingBonus = false;
      this.transitionToMode('bonus');
    }
  }

  // Process individual ball physics
  private processBall(ball: Ball): Ball {
    const processedBall = { ...ball };
    
    // Initialize ball if needed (always use normalized coordinates)
    if (ball.x === undefined || ball.y === undefined || ball.vx === undefined || ball.vy === undefined) {
      const rng = new SeededRandom(ball.randomSeed ^ 0x96cc6e6d);
      const spawnOffset = (rng.next() * 2 - 1) * SPAWN_OFFSET_FACTOR;
      // DO NOT DELETE. The value is unused, but the rng.next() call is not: dropping it shifts the
      // whole random stream by one and every trajectory in the game changes. Removing it as an
      // "unused variable" moved v1's fingerprint from 7:187:1 to 4:133:0 — caught by
      // lab/v4-engine-regress.ts, which is exactly what that check exists for.
      void (rng.next() * SPAWN_HEIGHT_FACTOR);
      const velocityX = (rng.next() * 2 - 1) * this.board.physics.initialVelocity;

      const PIN_SPACING_X = this.board.spacingX;
      const BALL_RADIUS = this.board.physics.ballRadius;
      
      processedBall.x = CANVAS_WIDTH / 2 + (spawnOffset * PIN_SPACING_X);
      processedBall.y = BALL_RADIUS * 5; // -BALL_RADIUS * (1 + spawnHeight);
      processedBall.vx = velocityX;
      processedBall.vy = 0;
    }

    // Physics update
    const currentX = processedBall.x!;
    const currentY = processedBall.y!;
    const currentVy = processedBall.vy!;

    // Apply gravity
    processedBall.vy = currentVy + this.board.physics.gravity;
    // curve ball: a steady sideways push, integrated like gravity so it bends the whole descent
    if (processedBall.spin) processedBall.vx = (processedBall.vx ?? 0) + processedBall.spin;
    processedBall.x = currentX + processedBall.vx!;
    processedBall.y = currentY + processedBall.vy!;

    // Handle collisions
    this.handleCollisions(processedBall);

    // Solid sides, if this board has them
    this.handleWalls(processedBall);

    // Check if stuck
    this.handleStuckBall(processedBall);

    // Apply speed limits
    this.applySpeedLimits(processedBall);

    this.checkLetterActivation(processedBall, currentX, currentY);

    return processedBall;
  }

  private handleCollisions(ball: Ball): void {
    if (ball.collisionCooldown > 0) {
      ball.collisionCooldown -= 1;
      return;
    }

    // Use normalized coordinates (scale = 1)
    const PIN_RADIUS = this.board.physics.pinRadius;
    const SPECIAL_PIN_RADIUS = this.board.physics.specialPinRadius;
    const BALL_RADIUS = this.board.physics.ballRadius;

    // "bumpers off in the main game": collide against the plain field until a bonus starts.
    const activePins = (!this.board.spec.specialPinsInMain && this.state.mode === 'main')
      ? this.board.mainPins
      : this.pins;
    for (let p = 0; p < activePins.length; p++) {
      const pin = activePins[p];
      const ddx = ball.x! - pin.x;
      const ddy = ball.y! - pin.y;
      const distSq = ddx * ddx + ddy * ddy;
      const pinRadius = pin.isSpecial ? SPECIAL_PIN_RADIUS : PIN_RADIUS;
      const radiusSum = BALL_RADIUS + pinRadius;

      if (distSq < radiusSum * radiusSum) {
        const dist = Math.sqrt(distSq) || 0.001;
        const nx = ddx / dist;
        const ny = ddy / dist;
        const overlap = radiusSum - dist + SEPARATION_OVERLAP;

        // Separate
        ball.x! += nx * overlap;
        ball.y! += ny * overlap;

        // Handle collision physics
        const restitution = pin.isSpecial ? this.board.physics.specialRestitution : this.board.physics.normalRestitution;
        const vDotN = ball.vx! * nx + ball.vy! * ny;

        if (pin.isSpecial) {
          this.handleSpecialPinCollision(ball, nx, ny, vDotN, restitution);
        } else {
          this.handleNormalPinCollision(ball, nx, ny, vDotN, restitution);
        }

        // Set cooldown and effects
        ball.collisionCooldown = COLLISION_COOLDOWN_FRAMES;
        this.state.pinFx.set(p, PIN_SPARK_MAX_FRAMES);

        // Emit sound event
        if (this.state.pinSoundCooldown <= 0) {
          this.eventEmitter.emit({
            type: 'play-sound',
            payload: {
              soundType: pin.isSpecial ? 'special' : 'tick',
              volume: pin.isSpecial ? 0.5 : undefined
            }
          });
          this.state.pinSoundCooldown = pin.isSpecial ? 4 : 2;
        }

        break;
      }
    }
  }

  private handleSpecialPinCollision(ball: Ball, nx: number, ny: number, vDotN: number, restitution: number): void {
    // Anti-loop decay: after SPECIAL_PIN_EFFECT_CAP boosted hits this ball treats special pins
    // as normal (energy-absorbing) pins and stops counting special hits -> it settles and cannot
    // pump an endless bounce loop. Caps per-ball special-hit leveling at the cap.
    if (ball.currentSpecialHits >= SPECIAL_PIN_EFFECT_CAP) {
      this.handleNormalPinCollision(ball, nx, ny, vDotN, NORMAL_PIN_RESTITUTION);
      return;
    }
    const boostFactor = this.board.physics.specialBoost;
    
    ball.vx = ball.vx! - (1 + restitution) * vDotN * nx * boostFactor;
    ball.vy = ball.vy! - (1 + restitution) * vDotN * ny * boostFactor;

    // Floor the OUTWARD component so a glancing hit still reads as a bounce. Reflection alone
    // scales with the approach angle, which is why most bumper contacts were invisible: the median
    // hit changed the ball's speed by 1.05x. The existing clamp below bounds TOTAL speed, which a
    // ball skimming past a bumper already has plenty of.
    const kick = this.board.physics.specialKick;
    if (kick > 0) {
      const outward = ball.vx! * nx + ball.vy! * ny;
      if (outward < kick) {
        ball.vx = ball.vx! + (kick - outward) * nx;
        ball.vy = ball.vy! + (kick - outward) * ny;
      }
    }

    // Ensure minimum speed (normalized)
    const minSpeed = SPECIAL_PIN_MIN_SPEED_FACTOR;
    const currentSpeed = Math.sqrt(ball.vx! * ball.vx! + ball.vy! * ball.vy!);
    if (currentSpeed < minSpeed) {
      ball.vx = (ball.vx! / currentSpeed) * minSpeed;
      ball.vy = (ball.vy! / currentSpeed) * minSpeed;
    }

    // Handle special hit - always trigger on collision
    ball.currentSpecialHits++;
    this.eventEmitter.emit({
      type: 'special-pin-hit',
      payload: { expGained: this.state.mode === 'bonus' }
    });
  }

  private handleNormalPinCollision(ball: Ball, nx: number, ny: number, vDotN: number, restitution: number): void {
    if (vDotN < 0) {
      ball.vx = ball.vx! - (1 + restitution) * vDotN * nx;
      ball.vy = ball.vy! - (1 + restitution) * vDotN * ny;
    }

    // Tangential damping
    const tx = -ny, ty = nx;
    const vDotT = ball.vx! * tx + ball.vy! * ty;
    const damp = this.board.physics.tangentialDamping;
    const vTx = vDotT * tx * (1 - damp);
    const vTy = vDotT * ty * (1 - damp);
    ball.vx! -= vTx;
    ball.vy! -= vTy;
  }

  /**
   * Bounce off the board's sides.
   *
   * The barrier follows the silhouette rather than being a vertical line, so it is interpolated
   * between the sampled rows: at the neck the walls are close in, and they open out with the flare.
   * Without this a bumper kick just ejects the ball — 17.4% of bonus balls over the full seed space
   * — and the engine silently re-seeds and drops another one.
   */
  private handleWalls(ball: Ball): void {
    const w = this.board.walls;
    if (!w) return;
    const y = ball.y!;
    const { ys, left, right } = w;
    let k = 0;
    while (k < ys.length - 1 && ys[k + 1] < y) k++;
    let lx = left[k], rx = right[k];
    if (k < ys.length - 1) {
      const span = ys[k + 1] - ys[k];
      const t = span > 0 ? Math.min(1, Math.max(0, (y - ys[k]) / span)) : 0;
      lx = left[k] + (left[k + 1] - left[k]) * t;
      rx = right[k] + (right[k + 1] - right[k]) * t;
    }
    const r = this.board.physics.ballRadius;
    const e = this.board.physics.normalRestitution;
    if (ball.x! - r < lx) {
      ball.x = lx + r;
      if (ball.vx! < 0) ball.vx = -ball.vx! * e;
    } else if (ball.x! + r > rx) {
      ball.x = rx - r;
      if (ball.vx! > 0) ball.vx = -ball.vx! * e;
    }
  }

  private handleStuckBall(ball: Ball): void {
    const totalVelocity = Math.sqrt(ball.vx! * ball.vx! + ball.vy! * ball.vy!);
    
    if (totalVelocity < MIN_VELOCITY_THRESHOLD && ball.y! > 0) {
      ball.stuckCount++;
      const unstickRng = new SeededRandom(ball.randomSeed ^ (0xDEADBEEF + ball.stuckCount));
      ball.vx! += (unstickRng.next() - 0.5) * STUCK_VELOCITY_BOOST;
      ball.vy = Math.max(ball.vy!, STUCK_VELOCITY_BOOST);
    }
  }

  private applySpeedLimits(ball: Ball): void {
    const maxVx = MAX_HORIZONTAL_SPEED_FACTOR;
    const maxVy = MAX_VERTICAL_SPEED_FACTOR;
    
    ball.vx = Math.max(-maxVx, Math.min(maxVx, ball.vx!));
    ball.vy = Math.max(-maxVy, Math.min(maxVy, ball.vy!));
  }

  private handleBallReachedBottom(ball: Ball): void {
    // Calculate bin (normalized coordinates)
    const { binWidth, leftmostBinX, binCount } = this.board;
    const bin = Math.floor((ball.x! - leftmostBinX + binWidth / 2) / binWidth);
    const clampedBin = Math.max(0, Math.min(binCount - 1, bin));

    // outside
    if (bin != clampedBin) {
      this.eventEmitter.emit({
        type: 'ball-hit-bin',
        payload: null
      });
      return
    }

    // Emit event with just bin index
    this.eventEmitter.emit({
      type: 'ball-hit-bin',
      payload: {
        bin: clampedBin
      }
    });

    if (this.version === 'v2') {
      const letter = this.state.letters.find(l => l.binIndex === clampedBin);
      if (letter && !letter.active) {
        letter.active = true;
        this.eventEmitter.emit({
          type: 'letter-activated',
          payload: {
            letter: letter.letter,
            binIndex: clampedBin,
          },
        });

        if (this.state.letters.length > 0 && this.state.letters.every(l => l.active)) {
          this.pendingBonus = true;
        }
      }
    }
  }

  // Getters
  getBalls(): Ball[] {
    return this.state.balls;
  }

  getPinFx(): Map<number, number> {
    return this.state.pinFx;
  }

  getBallCount(): number {
    return this.state.balls.length;
  }
}
